/* Copyright (c) 2020-present CloudIO Inc */

import {
  ListItem,
  ListItemText,
  List,
  FormLabel,
  Theme,
  IconButton,
} from '@mui/material';
import VirtualList from 'components/DataGrid/grid/List';
import React, { CSSProperties } from 'react';
import { makeStyles } from '@mui/styles';
import Store from 'store/Store';
import { IoPageAccessV, Query } from '@cloudio-saas/datasource-types';
import useAppStore, { useRecord } from 'store/useAppStore';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import NoDataFound from 'utils/NoDataFound';
import Icon from 'icons/Icon';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import {
  sidebarActivePageIdAtom,
  sidebarPageAccessFilterAtom,
} from '../../atoms';
import Checkbox from '../fields/Checkbox';
import { getIoPageAccessVStoreProps } from 'store/utils/utils';
import { usePlatform } from 'platform/PlatformHooks';
import { isViewOnlyDeveloper } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    display: 'none',
  },
  listItem: {
    '&:hover': {
      '& > $editIcon': {
        display: 'unset',
      },
    },
  },
  check: {
    minWidth: 42,
    maxWidth: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

function AppCell({
  value,
  style,
  store,
  label,
  isViewOnlyUser = false,
}: {
  value: string;
  label?: string;
  style?: CSSProperties;
  store: Store<IoPageAccessV>;
  isViewOnlyUser: boolean;
}) {
  const classes = useStyles();
  const { record, update } = useRecord(store, value);

  return (
    <ListItem
      data-testid={value}
      className={classes.listItem}
      dense
      key={value}
      style={style}
    >
      <ListItemText primary={label || 'None'} />
      <Checkbox
        disabled={isViewOnlyUser}
        data-testid={`${record?.roleUid}-active`}
        checked={record?.active === 'Y'}
        className={classes.check}
        onChange={(e) => update({ active: e.target.checked ? 'Y' : 'N' })}
      />
      <Checkbox
        disabled={isViewOnlyUser}
        data-testid={`${record?.roleUid}-readOnly`}
        checked={record?.readOnly === 'N'}
        className={classes.check}
        onChange={(e) => update({ readOnly: e.target.checked ? 'N' : 'Y' })}
        sx={{ visibility: record?.active === 'Y' ? 'visible' : 'hidden' }}
      />
    </ListItem>
  );
}

const pgFilter: (appUid: string, pageUid: string) => Query<IoPageAccessV> = (
  appUid,
  pageUid,
) => ({
  filter: [],
  limit: 2000,
  params: { appUid, pageUid },
});

const PagesAccess = ({ appUid }: { appUid: string }): React.ReactElement => {
  const { store, records, recordsOrder } = useAppStore<IoPageAccessV>(
    getIoPageAccessVStoreProps(appUid),
  );
  const pageUid = useStateXAtomValue(sidebarActivePageIdAtom);
  store.read({ props: pgFilter(appUid, pageUid) });
  const classes = useStyles();

  const filter = useStateXAtomValue(sidebarPageAccessFilterAtom);
  let roles = recordsOrder;
  if (filter && records) {
    const lcfilter = filter.toLowerCase();
    roles = roles.filter(
      (id) =>
        (records[id]?.roleName?.toLowerCase().indexOf(lcfilter) ?? -1) !== -1,
    );
  }
  const { width, height } = useAutoSizerContext();
  const platform = usePlatform();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  if (roles.length === 0) {
    return (
      <NoDataFound
        message={
          filter ? `No roles found matching "${filter}"!` : 'No roles found!'
        }
      />
    );
  }

  return (
    <List>
      <ListItem dense style={{ width, height: 40 }}>
        <ListItemText primary="" />
        <FormLabel
          data-tip="Check all as Access Allowed"
          className={classes.check}
        >
          <IconButton
            data-testid="check-all-access-allowed"
            disabled={isViewOnlyUser}
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { active: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Check all as Edit Allowed"
          className={classes.check}
        >
          <IconButton
            data-testid="check-all-edit-allowed"
            disabled={isViewOnlyUser}
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { readOnly: 'N' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
      </ListItem>
      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="" />
        <FormLabel
          data-tip="Uncheck all Access Allowed"
          className={classes.check}
        >
          <IconButton
            data-testid="uncheck-all-access-allowed"
            disabled={isViewOnlyUser}
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { active: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck all
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck all Edit Allowed"
          className={classes.check}
        >
          <IconButton
            data-testid="uncheck-all-edit-allowed"
            disabled={isViewOnlyUser}
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { readOnly: 'Y' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck all
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
      </ListItem>

      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="Role" />
        <FormLabel data-tip="Access Allowed" className={classes.check}>
          <Icon iconName="eye" />
        </FormLabel>
        <FormLabel data-tip="Edit Allowed" className={classes.check}>
          <Icon iconName="edit" />
        </FormLabel>
      </ListItem>
      <VirtualList
        CellComponent={({ rowIndex, style }) => (
          <AppCell
            style={style}
            label={records[roles[rowIndex]].roleName}
            value={roles[rowIndex]}
            store={store}
            isViewOnlyUser={isViewOnlyUser}
          />
        )}
        rowHeight={40}
        height={height - 120 - 16}
        totalRows={roles.length}
        width={width}
        custom
      />
    </List>
  );
};

export default PagesAccess;
